import { withTag as withTag_ } from './tags.js';

// Process trigger types
export const TRIGGER_TYPES = [
  { key: 'time', label: 'Time-based', icon: '🕐' },
  { key: 'event', label: 'Event-based', icon: '⚡' },
  { key: 'manual', label: 'Manual', icon: '👆' },
];

export const SCHEDULE_TYPES = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

export const EVENT_TYPES = [
  { key: 'clock_in', label: 'Clock In', icon: '▶' },
  { key: 'clock_out', label: 'Clock Out', icon: '⏹' },
  { key: 'task_complete', label: 'Task Complete', icon: '✓' },
];

export const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Evaluate whether a process should fire given the current context.
 * Pure function — no Firestore.
 *
 * @param {object} process - The process document
 * @param {object} context - { now: ms, today: 'YYYY-MM-DD', event?: string, eventDetail?: { projectId, taskId } }
 * @returns {{ shouldFire: boolean, scheduledDate?: string }}
 */
export function shouldProcessFire(process, context) {
  if (!process || !process.enabled) return { shouldFire: false };

  const { trigger } = process;
  if (!trigger) return { shouldFire: false };

  // Manual processes never auto-fire
  if (trigger.type === 'manual') return { shouldFire: false };

  // Event-based triggers
  if (trigger.type === 'event') {
    if (!context.event || context.event !== trigger.event) return { shouldFire: false };
    // Optional project filter
    if (trigger.eventFilter?.projectId &&
        context.eventDetail?.projectId !== trigger.eventFilter.projectId) {
      return { shouldFire: false };
    }
    return { shouldFire: true, scheduledDate: context.today };
  }

  // Time-based triggers
  if (trigger.type === 'time') {
    // Already ran today?
    if (process.lastExecutedAt) {
      const lastRunDate = new Date(process.lastExecutedAt);
      const lastRunStr = `${lastRunDate.getFullYear()}-${String(lastRunDate.getMonth() + 1).padStart(2, '0')}-${String(lastRunDate.getDate()).padStart(2, '0')}`;
      if (lastRunStr === context.today) return { shouldFire: false };
    }

    if (trigger.schedule === 'daily') {
      return { shouldFire: true, scheduledDate: context.today };
    }

    if (trigger.schedule === 'weekly') {
      const dayOfWeek = new Date(context.today + 'T12:00:00').getDay();
      if (dayOfWeek !== trigger.dayOfWeek) return { shouldFire: false };
      return { shouldFire: true, scheduledDate: context.today };
    }

    if (trigger.schedule === 'monthly') {
      const dayOfMonth = new Date(context.today + 'T12:00:00').getDate();
      if (dayOfMonth !== trigger.dayOfMonth) return { shouldFire: false };
      return { shouldFire: true, scheduledDate: context.today };
    }
  }

  return { shouldFire: false };
}

/**
 * Build task template objects from a process's steps.
 * Pure function — returns objects ready for Firestore (caller assigns IDs).
 *
 * @param {object} process - The process document
 * @param {string} scheduledDate - YYYY-MM-DD
 * @param {string|null} contextProjectId - Fallback project ID from trigger context
 * @returns {Array<object>} Task templates with _stepId and _blockedBy metadata
 */
export function buildTasksFromSteps(process, scheduledDate, contextProjectId) {
  return (process.steps || [])
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((step, index) => ({
      _stepId: step.stepId,
      _blockedBy: step.blockedBy || null,
      title: step.title || '',
      description: step.description || '',
      projectId: step.projectId || process.projectId || contextProjectId || null,
      priority: step.priority || 'medium',
      estimatedMinutes: step.estimatedMinutes || null,
      timeBlock: step.timeBlock || null,
      taskType: step.taskType || 'task',
      billable: step.billable !== undefined ? step.billable : true,
      scheduledDate,
      sortOrder: Date.now() + index,
      _processId: process.id,
      _processName: process.name,
    }));
}

/**
 * Get a human-readable description of a process trigger.
 */
export function getTriggerLabel(trigger) {
  if (!trigger) return 'No trigger';
  if (trigger.type === 'manual') return 'Manual';
  if (trigger.type === 'event') {
    const evt = EVENT_TYPES.find(e => e.key === trigger.event);
    return evt ? `On ${evt.label}` : 'Event';
  }
  if (trigger.type === 'time') {
    if (trigger.schedule === 'daily') return `Daily${trigger.time ? ' at ' + trigger.time : ''}`;
    if (trigger.schedule === 'weekly') return `Weekly — ${DAYS_OF_WEEK[trigger.dayOfWeek] || ''}`;
    if (trigger.schedule === 'monthly') return `Monthly — day ${trigger.dayOfMonth || '?'}`;
  }
  return 'Unknown';
}

/**
 * Generate a unique step ID.
 */
export function generateStepId() {
  return 'step_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6);
}

/**
 * Create a default empty process object.
 */
export function makeProcess(overrides = {}) {
  return {
    name: '',
    description: '',
    icon: '🔄',
    color: '#8B5CF6',
    enabled: true,
    scope: 'global',
    projectId: null,
    trigger: {
      type: 'manual',
      schedule: null,
      time: null,
      dayOfWeek: null,
      dayOfMonth: null,
      event: null,
      eventFilter: null,
    },
    steps: [],
    lastExecutedAt: null,
    executionCount: 0,
    ...overrides,
  };
}

// ── Project automations (v0.1.247) ─────────────────────────────────────────────────────────────────────────────────
// Lj (Oct 11): "when a task is added here → set department / add tags / add to list template, when I clock in here →
// run the setup checklist / start routine R, when I clock out → run cleanup checklist, when a task is done → add tag /
// ask for a photo". Not a free-form rule engine: four fixed moments, each with a few toggles, on the project doc as
// one field `automations`. The department part is the project's existing lock / newTaskDepartment (Project settings →
// Department & type) — shown here, never stored twice. Pure.
export const PROJECT_AUTOMATION_EVENTS = [
  { key: 'onAdd', label: 'When a task is added here', icon: 'plus-circle' },
  { key: 'onClockIn', label: 'When I clock in here', icon: 'log-in' },
  { key: 'onClockOut', label: 'When I clock out', icon: 'log-out' },
  { key: 'onDone', label: 'When a task is done', icon: 'check-circle' },
];
export const DONE_PHOTO = [
  { key: 'off', label: 'No photo' },
  { key: 'ask', label: 'Ask for a photo' },
];

const tagList = (v) => (Array.isArray(v) ? v : []).reduce((acc, t) => withTag_(acc, t), []);
const idOrNull = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** The project's automations with every default filled in (setup + cleanup checklists on: today's behaviour). */
export function projectAutomations(project) {
  const a = project?.automations || {};
  return {
    onAdd: { tags: tagList(a.onAdd?.tags), listTemplateId: idOrNull(a.onAdd?.listTemplateId) },
    onClockIn: { setup: a.onClockIn?.setup !== false, routineId: idOrNull(a.onClockIn?.routineId) },
    onClockOut: { cleanup: a.onClockOut?.cleanup !== false, routineId: idOrNull(a.onClockOut?.routineId) },
    onDone: { tags: tagList(a.onDone?.tags), photo: a.onDone?.photo === 'ask' ? 'ask' : 'off' },
  };
}

/** What to store: only what differs from the defaults (null = all defaults → the field is cleared). */
export function cleanAutomations(input) {
  const a = projectAutomations({ automations: input });
  const out = {};
  if (a.onAdd.tags.length || a.onAdd.listTemplateId) out.onAdd = { ...(a.onAdd.tags.length ? { tags: a.onAdd.tags } : {}), ...(a.onAdd.listTemplateId ? { listTemplateId: a.onAdd.listTemplateId } : {}) };
  if (!a.onClockIn.setup || a.onClockIn.routineId) out.onClockIn = { ...(!a.onClockIn.setup ? { setup: false } : {}), ...(a.onClockIn.routineId ? { routineId: a.onClockIn.routineId } : {}) };
  if (!a.onClockOut.cleanup || a.onClockOut.routineId) out.onClockOut = { ...(!a.onClockOut.cleanup ? { cleanup: false } : {}), ...(a.onClockOut.routineId ? { routineId: a.onClockOut.routineId } : {}) };
  if (a.onDone.tags.length || a.onDone.photo === 'ask') out.onDone = { ...(a.onDone.tags.length ? { tags: a.onDone.tags } : {}), ...(a.onDone.photo === 'ask' ? { photo: 'ask' } : {}) };
  return Object.keys(out).length ? out : null;
}

/** One moment changed: the project's automations with `patch` merged into `event` → the value to store. */
export function setAutomation(project, event, patch) {
  const cur = projectAutomations(project);
  if (!cur[event]) throw new Error(`no automation moment "${event}"`);
  return cleanAutomations({ ...cur, [event]: { ...cur[event], ...patch } });
}

/** Does the project run its setup / cleanup checklist (bucket) on the clock? Default yes. */
export const automationRunsBucket = (project, bucket) => (bucket === 'cleanup' ? projectAutomations(project).onClockOut.cleanup : bucket === 'setup' ? projectAutomations(project).onClockIn.setup : true);

/** The routine to start on clock in / out at this project, or null. */
export const automationRoutine = (project, event) => (event === 'clock_in' ? projectAutomations(project).onClockIn.routineId : event === 'clock_out' ? projectAutomations(project).onClockOut.routineId : null);

// ── Department task defaults (v0.1.247) ────────────────────────────────────────────────────────────────────────────
// Lj (Oct 11): "set up task defaults per department — e.g. photos — so we don't have to make everything a service item,
// it just does it". On the opsDepartments doc as one field `taskDefaults`. Applied at capture (after inference picks the
// department), at done and at clock-out.
export const DEPT_PHOTO = [
  { key: 'none', label: 'No photo', hint: 'Nothing asked.' },
  { key: 'after', label: 'After photo', hint: 'Done asks for a photo of the finished work.' },
  { key: 'before_after', label: 'Before + after', hint: 'Done asks for the after photo; clock-out reminds you of tasks worked today with no before / after photo.' },
];
export const ESTIMATE_UNITS = ['each', 'hr', 'sqft', 'lf', 'sheet', 'room', 'door', 'window', 'page', 'post', 'video'];

/** A department's task defaults with every default filled in. */
export function deptTaskDefaults(dept) {
  const d = dept?.taskDefaults || {};
  return {
    tags: tagList(d.tags),
    photo: DEPT_PHOTO.some((p) => p.key === d.photo) ? d.photo : 'none',
    unit: typeof d.unit === 'string' && d.unit.trim() ? d.unit.trim().slice(0, 16) : null,
    listTemplateId: idOrNull(d.listTemplateId),
    noteOnDone: d.noteOnDone === true,
  };
}

/** What to store (null = nothing set). */
export function cleanTaskDefaults(input) {
  const d = deptTaskDefaults({ taskDefaults: input });
  const out = {};
  if (d.tags.length) out.tags = d.tags;
  if (d.photo !== 'none') out.photo = d.photo;
  if (d.unit) out.unit = d.unit;
  if (d.listTemplateId) out.listTemplateId = d.listTemplateId;
  if (d.noteOnDone) out.noteOnDone = true;
  return Object.keys(out).length ? out : null;
}

/** Defaults set at all? (for a "3 set" summary) → count. */
export const taskDefaultsCount = (dept) => { const c = cleanTaskDefaults(dept?.taskDefaults); return c ? Object.keys(c).length : 0; };

const listFromTemplate = (tpl) => (tpl && Array.isArray(tpl.items) && tpl.items.length
  ? { templateId: tpl.id, name: tpl.name || null, items: tpl.items.filter((x) => x && String(x.text || '').trim()).map((x) => ({ text: String(x.text).trim(), ...(Number(x.qty) > 0 ? { qty: Number(x.qty) } : {}) })) }
  : null);

/**
 * At capture, after the department is known: the fields the department's defaults and the project's "when a task is
 * added" add to a new top-level task. Tags join (the task's own first), a list only when the task has none, the unit
 * only when the task has none. → { fields, applied: ['#tags', 'list', 'unit'] } (fields = {} when nothing applies).
 */
export function captureDefaults(task, { dept = null, project = null, listTemplates = [] } = {}) {
  if (!task || task.parentId || task.bucket || task.runId) return { fields: {}, applied: [] };
  const d = deptTaskDefaults(dept);
  const a = projectAutomations(project);
  const fields = {};
  const applied = [];
  const tags = [...d.tags, ...a.onAdd.tags].reduce((acc, t) => withTag_(acc, t), Array.isArray(task.tags) ? task.tags : []);
  if (tags.length !== (task.tags || []).length) { fields.tags = tags; applied.push('tags'); }
  const hasList = task.list && Array.isArray(task.list.items) && task.list.items.length;
  const tplId = a.onAdd.listTemplateId || d.listTemplateId;
  if (!hasList && tplId) {
    const list = listFromTemplate((listTemplates || []).find((t) => t && t.id === tplId));
    if (list) { fields.list = list; applied.push('list'); }
  }
  if (!task.unit && d.unit) { fields.unit = d.unit; applied.push('unit'); }
  if (d.photo !== 'none' && !task.requiresAfterPhoto) { fields.requiresAfterPhoto = true; applied.push('photo'); }
  if (d.photo === 'before_after' && !task.requiresBeforePhoto) fields.requiresBeforePhoto = true;
  if (d.noteOnDone && !task.noteOnDone) { fields.noteOnDone = true; applied.push('note'); }
  return { fields, applied };
}

/**
 * When a task is done: what the project's "when a task is done" and the department's defaults ask.
 * → { tags: the task's tags + the project's done tags (null = no change), photo: ask for an after photo, note: ask for a note }
 */
export function doneRules(task, { dept = null, project = null } = {}) {
  if (!task || task.bucket || task.runId) return { tags: null, photo: false, note: false };
  const a = projectAutomations(project);
  const d = deptTaskDefaults(dept);
  const have = Array.isArray(task.tags) ? task.tags : [];
  const tags = a.onDone.tags.reduce((acc, t) => withTag_(acc, t), have);
  const hasAfter = !!(task.afterPhoto || (Array.isArray(task.afterPhotos) && task.afterPhotos.length));
  return {
    tags: tags.length !== have.length ? tags : null,
    photo: !hasAfter && (a.onDone.photo === 'ask' || (!task.parentId && d.photo !== 'none')),
    note: !task.parentId && (task.noteOnDone === true || d.noteOnDone),
  };
}

/**
 * Clock-out reminder (department "Before + after"): tasks in such departments worked today with no before or no after
 * photo. deptOf(task) → the department doc. → [{ id, title, missing: ['before', 'after'] }].
 */
export function photoGaps(tasks = [], { date, deptOf, media = [] } = {}) {
  const tagsOf = (id) => new Set((media || []).filter((m) => m && m.taskId === id && m.kind !== 'voice').map((m) => m.tag));
  const out = [];
  for (const t of tasks) {
    if (!t || t.parentId || t.bucket || t.deletedAt) continue;
    const worked = (t.timeEntries || []).some((e) => e && e.date === date) || (t.status === 'done' && t.completedAt && localDay(t.completedAt) === date);
    if (!worked) continue;
    const d = deptTaskDefaults(deptOf ? deptOf(t) : null);
    if (d.photo !== 'before_after') continue;
    const shots = Array.isArray(t.shots) ? t.shots : [];
    const idx = tagsOf(t.id);
    const before = !!t.photo || shots.some((s) => s && s.tag === 'before') || idx.has('before');
    const after = !!t.afterPhoto || (Array.isArray(t.afterPhotos) && t.afterPhotos.length > 0) || shots.some((s) => s && s.tag === 'after') || idx.has('after');
    const missing = [...(before ? [] : ['before']), ...(after ? [] : ['after'])];
    if (missing.length) out.push({ id: t.id, title: t.title || 'Task', missing });
  }
  return out;
}
const localDay = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
